/* eslint-disable react/no-multi-comp */

import React from 'react';
import PropTypes from 'prop-types';

import FormContext from 'common/components/form/context';
import FormField from 'common/components/form/components/form-field';
import {FormThemeEnum, connectToForm} from 'common/components/form/utils';

// FormFieldProvider
//
// Context provider for form fields.
//
// Converts a serialized form into UI, exposing the fields for consumption
// by FormFieldConsumer component.
//
// May be a local form or a remote form, which uses our remote_form backend.
//
// @param formId: An id for the form, which must match the redux
//    store key holding the form data.
// @param form: A serialized form. Follows our remote_form backend payload schema.
// @param url: An endpoint for a remote_form backend. Omit to use a local form.
//
// Usage:
// <Provider store={store}>
//    <FormFieldProvider
//      formId={keyMatchesReduxStore}
//      url={remoteUrlReturnsFormJson}
//    >
//      <div class={'simple-box'}>
//        <h1>hi, whats your name?</h1>
//        <FormFieldConsumer name={'first_name'}>
//      </div>
//
//      <div class={'whacky-box'}>
//        <h1>here's some other stuff as well</h1>
//        <SvgIcon icon={dongle} />
//        <FormFieldConsumer name={'some_other_field_name'}>
//      </div>
//    </FormFieldProvider>
// </Provider>
//
class FormFieldProvider extends React.Component {
  static propTypes = {
    // anything that wants to render a connected form or field
    children: PropTypes.any,

    // whether or not the form field should be disabled
    disabled: PropTypes.bool,

    // make GET request for initial form and set form data in redux
    fetchFormData: PropTypes.func,

    // pass `form` instead of `url` prop for non-remote forms
    form: PropTypes.shape({
      prefix: PropTypes.string,
      data: PropTypes.object,
      errors: PropTypes.object,
      fields: PropTypes.object,
      ordered_fields: PropTypes.array,
    }),

    // Formset child forms must pass this reference to their parent
    parentForm: PropTypes.object,

    // make POST request on change and set form data in redux
    postFormData: PropTypes.func,

    // set form data in redux
    setFormData: PropTypes.func,

    // theme of the form. 'legacy' or 'fusion'; defaults to 'legacy'.
    theme: PropTypes.oneOf(Object.values(FormThemeEnum)),

    // set individual field value in form data in redux
    updateFormValue: PropTypes.func,

    // pass `url` instead of `form` prop for a remote form
    url: PropTypes.string,
  };

  static defaultProps = {
    theme: FormThemeEnum.LEGACY,
  };

  componentDidMount() {
    const {fetchFormData, form, parentForm, setFormData, url} = this.props;

    if (parentForm) {
      // initial state set by parent
      return;
    }

    if (url) {
      fetchFormData(url);
    } else {
      setFormData(form);
    }
  }

  getFieldsByName() {
    const {form, theme} = this.props;
    if (!form) {
      return {};
    }

    const {prefix} = form;

    const fieldsByName = {};
    for (const name of form.ordered_fields || []) {
      const data = form.data[name];
      const errors = form.errors && form.errors[name];
      const field = form.fields[name];
      const disabled = this.props.disabled || field.disabled;
      const affects = Boolean(field.widget.attrs['data-affects']);
      fieldsByName[name] = (
        <FormField
          data={data}
          disabled={disabled}
          errors={errors}
          field={field}
          handleChange={(value) => this.handleChange(name, value, affects)}
          key={prefix ? `${prefix}-${name}` : name}
          name={name}
          theme={theme}
        />
      );
    }

    return fieldsByName;
  }

  render() {
    const fieldsByName = this.getFieldsByName();
    return (
      <FormContext.Provider value={fieldsByName}>
        {this.props.children}
      </FormContext.Provider>
    );
  }

  handleChange(name, value, affects) {
    const {form, parentForm, postFormData, updateFormValue, url} = this.props;

    if (affects) {
      postFormData({
        changedFieldName: name,
        changedFieldValue: value,
        form: parentForm || form,
        childFormPrefix: form.prefix,
        url,
      });
    } else {
      updateFormValue(name, value, form.prefix);
    }
  }
}

/*
 * Decorator that tranforms `form` prop by parsing data for
 * fields that have a custom data structure, where data may
 * be a primitive or JSON
 */
function withCustomDataStructure(WrappedForm) {
  class ParsedForm extends React.Component {
    render() {
      const props = {...this.props};
      const {form} = props;

      if (form) {
        const {data} = form;
        for (const [name, field] of Object.entries(form.fields)) {
          if (!field.custom_data_structure) {
            continue;
          }

          const value = data[name];
          if (typeof value !== 'string') {
            continue;
          }

          try {
            data[name] = JSON.parse(value);
          } catch {
            // some other non-json string was passed,
            // so let it error in prop types validation
          }
        }
      }

      return <WrappedForm {...props} />;
    }
  }

  return React.memo(ParsedForm);
}

export default withCustomDataStructure(connectToForm(FormFieldProvider));
